import { gsap } from "gsap";

/**
 * Custom Cursor
 * ----------------------------------------------------------------------------
 * Cerchio outline + freccia che segue il mouse e cambia stato in base
 * all'elemento sotto al cursore.
 *
 * Stati auto-rilevati via selettori CSS:
 *   - link:   a, button, [role="button"]      → rotazione -45° (↗)
 *   - slider: .swiper, .swiper-container, [data-slider]  → rotazione 0° (→)
 *   - scroll: [data-percorso-block]           → rotazione 90° (↓)
 *   - default: invisibile (nessun elemento target sotto)
 *
 * Disattivato su:
 *   - touch device (hover: none) → display:none via CSS, JS comunque return early
 *   - prefers-reduced-motion → idem
 *   - WP admin (Gutenberg editor) → check body.wp-admin
 *
 * Movimento: gsap.quickSetter + interpolazione lerp per follow morbido.
 */

const CURSOR_STATES = [
  { name: "link", selector: "a, button, [role='button']", rotation: -45 },
  { name: "slider", selector: ".swiper, .swiper-container, [data-slider]", rotation: 0 },
  { name: "scroll", selector: "[data-percorso-block]", rotation: 90 },
];

const LERP_FACTOR = 0.18; // 0=no follow, 1=instant. ~0.18 = morbido senza lag eccessivo

const isTouchDevice = () =>
  window.matchMedia("(hover: none), (pointer: coarse)").matches;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const initCustomCursor = () => {
  // Skip se touch, reduced motion, o WP admin
  if (isTouchDevice() || prefersReducedMotion()) return;
  if (document.body.classList.contains("wp-admin")) return;

  const cursor = document.querySelector("[data-custom-cursor]");
  if (!cursor) return;

  const arrow = cursor.querySelector("[data-custom-cursor-arrow]");

  // Marker sul body: lo scss può scegliere di reagire (es. nascondere cursor reale)
  document.body.classList.add("has-custom-cursor");

  // Posizione corrente (animata) e target (mouse reale)
  const pos = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  const target = { x: pos.x, y: pos.y };

  // QuickSetter per perf (vs gsap.set ad ogni frame)
  const setX = gsap.quickSetter(cursor, "x", "px");
  const setY = gsap.quickSetter(cursor, "y", "px");

  // Stato corrente (per evitare tween ridondanti)
  let currentState = "hidden";

  // Timer per debounce dello stato "hidden": evita flickering quando il mouse
  // attraversa brevemente elementi non-target (es. spazio tra 2 link adiacenti).
  // Se entro HIDE_DELAY entriamo in un nuovo elemento target, il fade-out
  // viene cancellato.
  const HIDE_DELAY = 100; // ms
  let hideTimer = null;

  // Update loop: interpolazione tra pos e target
  gsap.ticker.add(() => {
    pos.x += (target.x - pos.x) * LERP_FACTOR;
    pos.y += (target.y - pos.y) * LERP_FACTOR;
    setX(pos.x);
    setY(pos.y);
  });

  // Listener mousemove: aggiorna target
  window.addEventListener("mousemove", (e) => {
    target.x = e.clientX;
    target.y = e.clientY;
  });

  // Rileva stato in base all'elemento sotto al mouse
  const detectState = (el) => {
    if (!el) return "hidden";
    for (const state of CURSOR_STATES) {
      if (el.closest(state.selector)) return state.name;
    }
    return "hidden";
  };

  // Applica stato (visibile/invisibile + rotazione icona)
  const applyState = (newState) => {
    // Se il nuovo stato è un target valido, cancella eventuale fade-out pendente
    if (newState !== "hidden" && hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }

    if (newState === currentState) return;

    // Debounce sullo "hidden": evita flickering tra elementi target adiacenti
    if (newState === "hidden") {
      if (hideTimer) return; // già in attesa di nascondere
      hideTimer = setTimeout(() => {
        hideTimer = null;
        if (currentState === "hidden") return;
        currentState = "hidden";
        gsap.to(cursor, {
          opacity: 0,
          scale: 0.4,
          duration: 0.3,
          ease: "power2.out",
          onComplete: () => gsap.set(cursor, { visibility: "hidden" }),
        });
      }, HIDE_DELAY);
      return;
    }

    currentState = newState;

    // Stato attivo: visibile + rotazione icona
    const stateConfig = CURSOR_STATES.find((s) => s.name === newState);
    if (!stateConfig) return;

    gsap.set(cursor, { visibility: "visible" });
    gsap.to(cursor, {
      opacity: 1,
      scale: 1,
      duration: 0.35,
      ease: "power2.out",
    });

    if (arrow) {
      gsap.to(arrow, {
        rotation: stateConfig.rotation,
        duration: 0.4,
        ease: "power2.out",
      });
    }
  };

  // Listener mouseover (cattura ogni hover su qualsiasi elemento)
  document.addEventListener("mouseover", (e) => {
    applyState(detectState(e.target));
  });

  // Esce dalla finestra → hidden
  document.addEventListener("mouseleave", () => applyState("hidden"));
  document.addEventListener("mouseout", (e) => {
    // Se il mouse esce dalla viewport, !e.relatedTarget = true
    if (!e.relatedTarget) applyState("hidden");
  });
};

export default () => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initCustomCursor);
  } else {
    initCustomCursor();
  }
};